import { apiSlice } from './apiSlice';

/* Game catalog item as returned by the aggregator's /v1/games (proxied via our backend).
   Real API fields: gameCode (globally-unique, used for launch), gameProvider (studio
   display name), thumbnailUrl (may be null). Launch keys on gameCode alone. */
export interface AggregatorGame {
  id: string;
  name: string;
  category?: string;
  gameProvider?: string;
  rtp?: number;
  gameCode: string;
  thumbnailUrl?: string | null;
}

export interface AggregatorGamesResponse {
  items: AggregatorGame[];
  total: number;
  page: number;
  pageSize: number;
}

export interface AggregatorGamesParams {
  providerCode?: string;
  vendorName?: string;
  category?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface AggregatorHistoryItem {
  providerTxId: string;
  type: 'DEBIT' | 'CREDIT' | 'ROLLBACK' | 'SETTLE';
  amount: string;
  currency: string;
  balanceBefore?: string;
  balanceAfter?: string;
  status: string;
  createdAt: string;
  provider?: { code: string };
  game?: { name: string; providerGameCode: string };
}

export const AGGREGATOR_FALLBACK_ART = '/images/casino/casino-fallback.webp';

/**
 * Resolves a game's artwork to a URL the browser can load.
 *
 * `thumbnailUrl` is NOT an upstream link — it is a path on our own API
 * (/aggregator/image/…) that resizes the provider's originals, which run up to
 * 470 KB each. So it has to carry the same origin the API slice talks to.
 * Always go through this rather than using `thumbnailUrl` directly.
 */
const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

export const aggregatorArtUrl = (thumbnailUrl?: string | null) =>
  thumbnailUrl ? `${API_BASE}${thumbnailUrl}` : AGGREGATOR_FALLBACK_ART;

export interface AggregatorProvider {
  brand_id: number;
  name: string;
  logo?: string;
  game_count: number;
}

export const aggregatorApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Studios enabled on our account — drives the provider tab row.
    getAggregatorProviders: builder.query<
      { count: number; providers: AggregatorProvider[] },
      void
    >({
      query: () => ({ url: '/aggregator/providers', method: 'GET' }),
    }),

    // Browse the aggregated catalog (only studios enabled for our platform).
    getAggregatorGames: builder.query<AggregatorGamesResponse, AggregatorGamesParams>({
      query: (params) => {
        const q = new URLSearchParams();
        Object.entries(params ?? {}).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
        });
        return { url: `/aggregator/games${q.toString() ? `?${q}` : ''}`, method: 'GET' };
      },
    }),

    // The curated home-page lineup, picked and ordered on the backend so the
    // strip is a merchandising decision rather than "whatever page 1 returns".
    getAggregatorHomeGames: builder.query<{ items: AggregatorGame[]; total: number }, void>({
      query: () => ({ url: '/aggregator/home-games', method: 'GET' }),
    }),

    // Open a game session — backend calls /v1/launch and returns { launchUrl }.
    // Keys on gameCode alone; providerCode is not needed by the aggregator.
    launchAggregatorGame: builder.mutation<
      { launchUrl: string },
      { gameCode: string; currency?: string; language?: string }
    >({
      query: (body) => ({ url: '/aggregator/launch', method: 'POST', body }),
    }),

    // Bet/round history for the logged-in player (reconciliation / support).
    getAggregatorHistory: builder.query<
      { items: AggregatorHistoryItem[]; total: number; page: number; pageSize: number },
      { page?: number; pageSize?: number }
    >({
      query: ({ page = 1, pageSize = 50 } = {}) => ({
        url: `/aggregator/history?page=${page}&pageSize=${pageSize}`,
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useGetAggregatorHomeGamesQuery,
  useGetAggregatorProvidersQuery,
  useGetAggregatorGamesQuery,
  useLaunchAggregatorGameMutation,
  useGetAggregatorHistoryQuery,
} = aggregatorApi;
